import React, { useEffect, useRef } from "react";
import experiences from "../data/experiences";

const ExperienceCard = ({ experience, index, isLast }) => {
  const cardRef = useRef(null);
  const isEven = index % 2 === 0;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
          }
        });
      },
      { threshold: 0.1 },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="relative opacity-0"
      style={{ animationDelay: `${index * 150}ms` }}
    >
      {/* Timeline dot avec animation de pulsation */}
      <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-white border-4 border-primary rounded-full top-8 shadow-lg z-10">
        <div className="absolute inset-0 rounded-full bg-white opacity-20"></div>
      </div>

      <div className={`flex ${isEven ? "justify-start" : "justify-end"} mb-16`}>
        <div className={`w-full lg:w-5/12 ${isEven ? "lg:pr-12" : "lg:pl-12"}`}>
          <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-100 transition-all duration-300 backdrop-blur-sm">
            {/* Date badge avec effet glassmorphism */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 via-blue-500/10 to-blue-600/10 border border-primary/20 mb-6 backdrop-blur-sm">
              <div className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></div>
              <svg
                className="w-4 h-4 mr-2 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="text-primary font-semibold text-sm">
                {experience.date}
              </span>
            </div>

            {/* Content sans hover ni animation subtile */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {experience.title}
                {experience.complement && (
                  <span className="text-lg font-normal text-blue-600 ml-2 opacity-80">
                    ({experience.complement})
                  </span>
                )}
              </h3>

              {experience.entreprise && (
                <div className="flex items-center mb-4">
                  <div>
                    <span className="font-bold text-gray-800 text-lg">
                      {experience.entreprise}
                    </span>
                    {experience.lieu && (
                      <div className="flex items-center text-gray-600 text-sm">
                        <svg
                          className="w-3 h-3 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                        </svg>
                        {experience.lieu}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <p className="text-gray-700 leading-relaxed mb-6 text-base">
                {experience.description}
              </p>

              {experience.technologies &&
                experience.technologies.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">
                      Technologies
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {experience.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 border border-blue-200 cursor-default"
                          style={{ animationDelay: `${techIndex * 50}ms` }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary/5 to-blue-600/5 rounded-full -mr-10 -mt-10 transition-transform duration-500"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Experiences = () => {
  return (
    <div className="pt-24 lg:pt-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>
      <div className="absolute top-32 left-10 w-32 h-32 bg-primary/5 rounded-full animate-pulse"></div>
      <div
        className="absolute top-40 right-20 w-24 h-24 bg-blue-500/5 rounded-full animate-bounce"
        style={{ animationDelay: "1s" }}
      ></div>
      <div
        className="absolute bottom-40 left-20 w-40 h-40 bg-indigo-500/5 rounded-full animate-pulse"
        style={{ animationDelay: "2s" }}
      ></div>

      <div className="relative lg:mx-auto lg:max-w-7xl lg:w-full lg:pb-20">
        <div className="lg:px-8 px-4">
          <div className="text-center mb-16">
            <h1 className="lg:tracking-tight font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-600 to-indigo-600 lg:text-6xl text-4xl mb-6">
              Mes Expériences
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Mon parcours professionnel et les expériences qui ont façonné mes
              compétences
            </p>
          </div>

          <div className="relative lg:block hidden timeline-container">
            {experiences.map((experience, index) => (
              <ExperienceCard
                key={index}
                experience={experience}
                index={index}
                isLast={index === experiences.length - 1}
              />
            ))}
          </div>

          <div className="lg:hidden space-y-6">
            {experiences.map((experience, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
              >
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-primary/10 to-blue-600/10 border border-primary/20 mb-4">
                  <svg
                    className="w-4 h-4 mr-2 text-primary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-primary font-medium text-sm">
                    {experience.date}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {experience.title}
                  {experience.complement && (
                    <span className="text-sm font-normal text-gray-600 ml-2">
                      ({experience.complement})
                    </span>
                  )}
                </h3>

                {experience.entreprise && (
                  <div className="flex items-center mb-3">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H7m2 0v-6a2 2 0 012-2h2a2 2 0 012 2v6"
                      />
                    </svg>
                    <span className="font-semibold text-gray-800">
                      {experience.entreprise}
                    </span>
                    {experience.lieu && (
                      <span className="text-gray-500 ml-2">
                        • {experience.lieu}
                      </span>
                    )}
                  </div>
                )}

                <p className="text-gray-700 leading-relaxed mb-4">
                  {experience.description}
                </p>

                {experience.technologies &&
                  experience.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {experience.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Experiences;
